import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { getOrderData } from '../../store/appointment/appointmentActions';
import { getImage } from '../../utils';
import moment from 'moment';
import { FiAlertCircle, FiCalendar, FiCheck, FiChevronRight, FiHome, FiX } from 'react-icons/fi';
import { COURSES_URL, DASHBOARD_URL } from '../../components/app/routes';

const formatMoney = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(2) : '—';
};

const normalizeOrder = (raw) => ({
    ...raw,
    appointments: (raw.appointments ?? []).map((row) => ({
        ...row,
        dateLabel: moment(row.date).local().format('ddd D MMM YYYY, HH:mm'),
    })),
});

const StatusHeader = ({ approved }) => (
    <div
        className={`relative overflow-hidden px-6 py-8 text-center ${
            approved ? 'bg-gradient-to-br from-nt-blue to-[#252a6e]' : 'bg-gradient-to-br from-nt-red to-[#b82238]'
        }`}
    >
        <div
            className={`mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full border-2 border-white/30 bg-white/10 backdrop-blur-sm ${
                approved ? 'text-green' : 'text-white'
            }`}
        >
            {approved ? <FiCheck className="h-8 w-8" strokeWidth={2.5} /> : <FiX className="h-8 w-8" strokeWidth={2.5} />}
        </div>
        <h1 className="text-xl font-semibold tracking-tight text-white sm:text-2xl">
            {approved ? 'Payment confirmed' : 'Payment not completed'}
        </h1>
        <p className="mt-2 max-w-md mx-auto text-sm text-white">
            {approved
                ? 'Your lesson slots are booked. You will find them in your appointments.'
                : 'No charge was finalized. You can try again from the tutor profile when you are ready.'}
        </p>
    </div>
);

const PaymentResult = () => {
    const { id } = useParams();
    const dispatch = useDispatch();
    const [order, setOrder] = useState(null);
    const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'

    const loadOrder = useCallback(() => {
        if (!id) {
            setStatus('error');
            return;
        }
        setStatus('loading');
        dispatch(getOrderData(id))
            .unwrap()
            .then((raw) => {
                setOrder(normalizeOrder(raw));
                setStatus('ready');
            })
            .catch(() => {
                setOrder(null);
                setStatus('error');
            });
    }, [dispatch, id]);

    useEffect(() => {
        loadOrder();
    }, [loadOrder]);

    const approved = order?.status === 1;
    const tutorUser = order?.tutor?.userId;
    const appointmentCount = order?.appointments?.length ?? 0;
    const paypalPortion = useMemo(() => {
        if (!order) return 0;
        return Math.max(0, Number(order.total) - Number(order.balance ?? 0));
    }, [order]);

    if (status === 'loading') {
        return (
            <div className="min-h-[60vh] flex flex-col items-center justify-center px-4 py-12">
                <div className="w-full max-w-md animate-pulse space-y-4 rounded-2xl border border-nt-grey-light bg-white p-8 shadow-sm">
                    <div className="mx-auto h-16 w-16 rounded-full bg-nt-grey-light" />
                    <div className="h-6 rounded-lg bg-nt-grey-light" />
                    <div className="h-4 rounded bg-nt-grey-light/80" />
                    <div className="h-24 rounded-xl bg-nt-grey-light/60" />
                </div>
                <p className="mt-6 text-sm font-medium text-nt-grey">Loading receipt…</p>
            </div>
        );
    }

    if (status === 'error' || !order) {
        return (
            <div className="min-h-[50vh] flex flex-col items-center justify-center px-4 py-16 text-center">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-nt-red/10 text-nt-red">
                    <FiAlertCircle className="h-7 w-7" />
                </div>
                <h1 className="mt-5 text-xl font-semibold text-nt-blue">We could not load this order</h1>
                <p className="mt-2 max-w-sm text-sm text-nt-grey">
                    The link may be invalid or the order no longer exists. Check your wallet or appointment list.
                </p>
                <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                    <button
                        type="button"
                        onClick={loadOrder}
                        className="rounded-xl bg-nt-blue px-5 py-2.5 text-sm font-medium text-white transition hover:opacity-90"
                    >
                        Try again
                    </button>
                    <Link
                        to="/wallet"
                        className="inline-flex items-center gap-1 rounded-xl border border-nt-blue px-5 py-2.5 text-sm font-medium text-nt-blue transition hover:bg-nt-blue/5"
                    >
                        Wallet <FiChevronRight className="h-4 w-4" />
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-[70vh] bg-gradient-to-b from-nt-grey-light/80 to-white px-4 py-10 sm:py-14">
            <div className="mx-auto w-full max-w-lg overflow-hidden rounded-2xl border border-black/5 bg-white shadow-xl shadow-nt-blue/5">
                <StatusHeader approved={approved} />

                <div className="space-y-6 p-6 sm:p-8">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex items-center gap-4 min-w-0">
                            <img
                                className="h-14 w-14 shrink-0 rounded-full object-cover ring-2 ring-nt-grey-light"
                                src={getImage(tutorUser?.avatar?.path, 'AVATAR')}
                                alt=""
                            />
                            <div className="min-w-0">
                                <p className="text-xs font-semibold uppercase tracking-wide text-nt-grey">Tutor</p>
                                <p className="truncate text-lg font-semibold capitalize text-nt-blue">
                                    {[tutorUser?.firstName, tutorUser?.lastName].filter(Boolean).join(' ') || '—'}
                                </p>
                            </div>
                        </div>
                        <div className="shrink-0 rounded-xl bg-nt-grey-light/60 px-4 py-3 text-right">
                            <p className="text-xs font-medium uppercase tracking-wide text-nt-grey">Sessions</p>
                            <p className="text-lg font-bold text-nt-blue">
                                {appointmentCount}
                                <span className="text-sm font-semibold text-nt-grey">
                                    {appointmentCount === 1 ? ' slot' : ' slots'}
                                </span>
                            </p>
                        </div>
                    </div>

                    <div className="rounded-xl border border-nt-grey-light bg-nt-grey-light/30 p-4 sm:p-5">
                        <h2 className="text-xs font-semibold uppercase tracking-wide text-nt-grey">Amounts</h2>
                        <dl className="mt-3 space-y-2 text-sm">
                            <div className="flex justify-between gap-4 text-nt-blue">
                                <dt className="text-nt-grey">Wallet used</dt>
                                <dd className="font-semibold tabular-nums">{formatMoney(order.balance)} €</dd>
                            </div>
                            <div className="flex justify-between gap-4 text-nt-blue">
                                <dt className="text-nt-grey">Paid online</dt>
                                <dd className="font-semibold tabular-nums">{formatMoney(paypalPortion)} €</dd>
                            </div>
                            <div className="border-t border-nt-grey-light pt-3 flex justify-between gap-4">
                                <dt className="font-semibold text-nt-blue">Order total</dt>
                                <dd className="text-lg font-bold tabular-nums text-nt-red">{formatMoney(order.total)} €</dd>
                            </div>
                        </dl>
                    </div>

                    <div>
                        <div className="mb-3 flex items-center justify-between gap-2">
                            <h2 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-nt-blue">
                                <FiCalendar className="h-4 w-4 text-nt-red" aria-hidden />
                                Schedule
                            </h2>
                        </div>
                        <ul className="max-h-72 space-y-2 overflow-y-auto pr-1">
                            {order.appointments.map((row, i) => (
                                <li
                                    key={row._id ?? i}
                                    className="flex items-center justify-between gap-3 rounded-lg border border-transparent bg-nt-grey-light/50 px-3 py-2.5 text-sm text-nt-blue transition hover:border-nt-grey-light hover:bg-white"
                                >
                                    <span className="min-w-0 font-medium">{row.dateLabel}</span>
                                    <span className="shrink-0 font-semibold tabular-nums text-nt-red">
                                        {order.isFirstLesson && i === 0
                                            ? formatMoney(order.coursePrice / 2)
                                            : formatMoney(order.coursePrice)}{' '}
                                        €
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="flex flex-col gap-2 border-t border-nt-grey-light pt-6 sm:flex-row sm:justify-center">
                        <Link
                            to={COURSES_URL}
                            className="no-underline inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-nt-blue py-3 text-center text-sm font-semibold text-white transition hover:opacity-90 sm:flex-none sm:px-8"
                        >
                            My courses
                        </Link>
                        <Link
                            to={DASHBOARD_URL}
                            className="no-underline inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-nt-blue py-3 text-center text-sm font-semibold text-nt-blue transition hover:bg-nt-blue/5 sm:flex-none sm:px-8"
                        >
                            <FiHome className="h-4 w-4" aria-hidden />
                            Home
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { PaymentResult };
